// Consultancy overview card — header + multi-color donut + status list.

export const consultancyOverviewStyles = {
  card: {
    background: '#fff',
    border: '1px solid #e8eaee',
    borderRadius: '18px',
    boxShadow: '0 1px 2px rgba(20,30,55,.04)',
    overflow: 'hidden',
  },

  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 1.5,
    px: { xs: 2, md: 3 },
    py: 2.25,
    borderBottom: '1px solid #eef0f4',
  },

  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
  },

  headerIcon: {
    width: 38,
    height: 38,
    borderRadius: '12px',
    background: 'linear-gradient(135deg, rgba(124,92,240,0.20), rgba(124,92,240,0.08))',
    border: '1px solid rgba(124,92,240,0.22)',
    color: '#7c5cf0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 1px 2px rgba(124,92,240,0.10)',
  },

  headerTitle: {
    fontSize: '15px',
    fontWeight: 700,
    color: '#0f1424',
    letterSpacing: '-0.2px',
    lineHeight: 1.2,
  },

  headerSubtitle: {
    fontSize: '12.5px',
    color: '#626b78',
    mt: 0.25,
  },

  countBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.5,
    px: 1.25,
    py: 0.5,
    borderRadius: '999px',
    background: 'rgba(58,90,240,0.12)',
    color: '#3a5af0',
    fontSize: '12px',
    fontWeight: 700,
  },

  body: {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', md: '268px 1fr' },
    gap: { xs: 3, md: 3.5 },
    px: { xs: 2, md: 3 },
    py: 3,
  },

  donutWrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 2,
  },

  donutBox: (mounted = true) => ({
    position: 'relative',
    width: 184,
    height: 184,
    transform: mounted ? 'scale(1)' : 'scale(0.85)',
    opacity: mounted ? 1 : 0,
    transition: 'transform 0.7s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.5s ease',
    transformOrigin: 'center',
  }),

  donutCenter: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
  },

  donutCenterValue: {
    fontSize: '34px',
    fontWeight: 800,
    color: '#0f1424',
    lineHeight: 1,
    letterSpacing: '-1px',
    fontVariantNumeric: 'tabular-nums',
  },

  donutCenterLabel: {
    fontSize: '11.5px',
    fontWeight: 600,
    color: '#626b78',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    mt: 0.5,
  },

  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 1,
  },

  legendItem: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.6,
    fontSize: '11.5px',
    fontWeight: 600,
    color: '#3b4456',
  },

  legendSwatch: (color) => ({
    width: 10,
    height: 10,
    borderRadius: '3px',
    background: color,
    flexShrink: 0,
  }),

  list: {
    display: 'flex',
    flexDirection: 'column',
  },

  row: (isLast) => ({
    position: 'relative',
    display: 'grid',
    gridTemplateColumns: '4px 1fr auto auto',
    alignItems: 'center',
    gap: { xs: 1.25, md: 2 },
    py: 1.75,
    borderBottom: isLast ? 'none' : '1px solid #eef0f4',
  }),

  stripe: (color) => ({
    width: 4,
    height: 32,
    borderRadius: '4px',
    background: color,
  }),

  rowTextBox: {
    minWidth: 0,
  },

  rowTitle: {
    fontSize: '14px',
    fontWeight: 700,
    color: '#0f1424',
    lineHeight: 1.25,
    letterSpacing: '-0.1px',
  },

  rowSubtitle: {
    fontSize: '11.5px',
    color: '#626b78',
    mt: 0.25,
  },

  chipsWrap: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 0.75,
    justifyContent: 'flex-end',
    maxWidth: 320,
  },

  chip: (tone) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.6,
    pl: 0.85,
    pr: 0.4,
    py: 0.4,
    borderRadius: '999px',
    border: `1px solid ${tone}26`,
    background: `${tone}0d`,
    color: '#3b4456',
    fontSize: '11.5px',
    fontWeight: 600,
    lineHeight: 1,
    whiteSpace: 'nowrap',
    transition: 'transform 160ms ease, box-shadow 160ms ease, background 160ms ease',
    '&:hover': {
      background: `${tone}14`,
      boxShadow: `0 2px 6px ${tone}1f`,
      transform: 'translateY(-1px)',
    },
  }),

  chipDot: (tone) => ({
    width: 7,
    height: 7,
    borderRadius: '50%',
    background: tone,
    flexShrink: 0,
    boxShadow: `0 0 0 2px ${tone}1a`,
  }),

  chipName: {
    color: '#3b4456',
    fontWeight: 600,
    letterSpacing: '0.05px',
  },

  chipCount: (tone) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 20,
    height: 18,
    px: 0.65,
    borderRadius: '999px',
    background: tone,
    color: '#fff',
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: 0,
    boxShadow: `0 1px 2px ${tone}40`,
  }),

  rowValue: (color) => ({
    fontSize: '22px',
    fontWeight: 800,
    color,
    letterSpacing: '-0.6px',
    fontVariantNumeric: 'tabular-nums',
    minWidth: 32,
    textAlign: 'right',
  }),

  empty: {
    py: 6,
    textAlign: 'center',
    color: '#8b95a5',
    fontSize: '0.9rem',
  },
};
